import React from "react";
import ReadAloud from "../../components/ReadAloud/ReadAloud";

const HumanPractices = ({ fontSize }) => {
  return (
    <div className="page">
      <h1 className="page-title">Human Practices</h1>
      <ReadAloud>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          As the demand for recreational cannabis rises, it’s important to raise
          awareness about the environmental aspects of cannabis production. Our
          team aims to find a better manufacturing process for cannabis, while
          maintaining health benefits, and minimizing environmental impacts. The
          industry can only contribute to the overarching goal of building
          sustainable communities and taking on global climate action by
          responsibly producing and consuming cannabis.
        </div>
        <br></br>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Defining the Problem
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Cannabis was first legalized in California 26 years ago. In Canada,
          the government only recently legalized recreational use in 2018. The
          medical uses of cannabis have been more widely recognized in recent
          years and many derivative products have been developed to suit
          different consumers’ needs. Unfortunately, the traditional practice of
          cultivation is energy-intensive and inefficient. For crops to provide
          the best yield, factory farming sites may require more than 2000 watts
          per hour of electricity per square meter (Fertig & Bade, 2021). To put
          this into perspective, the average American household consumes 1240
          watts per hour (U.S. Energy Information Administration, 2022).
          Cannabis cultivation already accounts for 1% of the electricity
          consumption alone in the US and is steadily increasing (Mills &
          Zeramby, 2021).
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Even from a hydrological standpoint, traditional growing methods
          require 22.7L of water per plant per day during the growing season.
          With this, the water expenditure for the growth of cannabis is
          extremely high and economically poor (Bauer et al., 2015). Cannabis is
          one of the more demanding crops compared to other farmed crops such as
          wheat, corn and even soybeans (Drotleff, 2021). For more details,
          refer to Figure 1.
        </div>
        <img
          width="100%"
          alt="Resource comparison between cannabis and commodity crops"
          src="https://static.igem.wiki/teams/4393/wiki/human-practices/hp-crop-comparison.png"
        />
        <figcaption style={{fontSize: `${16 + fontSize}px`, textAlign: "center"}}>
          Figure 1: (Figure extracted from Zheng et al., 2021)
        </figcaption>
        <br></br>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Deforestation associated with cannabis production accentuates this
          impact, which can lead to a loss of biodiversity due to habitat
          removal (Butsic et al., 2018). If cultivation practice is done on
          public lands or unpermitted farms, the water is often diverted from
          streams. In 2018, there were a total of 14,000 unpermitted farms
          operating on federal and private lands in the United States alone
          (Carah et al., 2015). The runoff of large quantities of zinc,
          phosphorus, nitrogen and potassium and sulfur from these farms can
          lead to increased risk of algal bloom and damage to the freshwater
          aquatic ecosystem (GAIACA, 2020). This is concerning as there was an
          increase of 80% to 116% of cannabis cultivators around habitats that
          threaten endangered species from 2012 - 2016 (Butsic et al., 2018).
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          In terms of carbon dioxide emission, growing one ounce of cannabis
          indoors is about the same as burning 7 to 16 gallons of gasoline
          (Ashworth & Vizuete, 2017). Not only that, but cultivation emits
          volatile organic compounds (VOCs). These can interact with
          environmental contaminants to produce ozone, which is a toxic and
          unstable gas. Many indoor grow houses use carbon filtration systems to
          remove VOCs (GAIACA, 2020).
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Overall, as summarized by Brad Poulus, instructor at Toronto
          Metropolitan University, cannabis production is not an environmentally
          friendly process. Thus, it is imperative to find a more sustainable
          solution not only for countries where cannabis use is currently legal,
          but also for the future when cannabis use will be more common across
          the globe.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Our Solution
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          The biggest decision that our team made this year was choosing to do a
          project based on a controversial topic. Cannabis is legal in Canada,
          but it is still illegal and/or heavily stigmatized in many countries.
          This worried us from the start as iGEM is an international community
          and we wanted to be as inclusive as possible. Through our Human
          Practices activities, we determined that although cannabis is a
          controversial subject, this project will be beneficial in the
          long-term. Not only is it a potential solution to a local problem, but
          it also acts as a platform to start conversations on a difficult, but
          important issue. Cannabis is increasingly becoming accepted so we need
          to research better manufacturing practices to ensure sustainability in
          the industry.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Pinene Express aims to use yeast to produce cannabinoids and terpene,
          proving that it is totally doable, and with careful modeling, proving
          the process is industrially scalable. Our team has consulted various
          professors and startups regarding the environmental impacts of
          cannabis cultivation. Pinene Express is able to effectively eliminate
          many environmental impacts and waste emission hazards commonly seen in
          cannabis cultivation sites.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Integrated Human Practices
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          From a project design standpoint, we selected a project that none of
          our members had explicit experience with, so we relied heavily on the
          expertise of stakeholders, potential users, and other resources
          available at our institution to help develop our project. Here are
          some of the individuals and organizations that shaped our project
          throughout the year.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Project Ideation
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          In our initial project design, we envisioned our project to involve
          directly engineering the cannabis plant to achieve a higher yield.
          Upon speaking with Dr. Barbara Moffatt, a University of Waterloo
          professor that specializes in plant engineering, we received feedback
          that there are many regulations surrounding direct work with cannabis
          plants, even if it’s for a research project. In addition, plant
          engineering at the scale that we are considering is not particularly
          feasible within the timeline of an iGEM project. Upon reflection, we
          moved away from the cannabis plant altogether and instead used yeast
          as an alternate production method.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          The cannabis plant is a rich source of many different terpenes, so we
          couldn’t synthesize them all. To decide which terpenes we would focus
          on for our proof of concept, we emailed Shoppers Drug Mart. Their
          reply identified linalool, limonene, alpha pinene, and beta
          caryophyllene as the four terpenes that are the most abundant and of
          medical interest in cannabis plant extract. Since there have been many
          previous iGEM projects focusing on the production of linalool and
          limonene, we decided to tackle alpha pinene and beta caryophyllene,
          which are also representative of monoterpenes and sesquiterpenes,
          respectively.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Since our vision was to replicate the whole plant extract from
          cannabis, we originally planned on having one strain of yeast that
          toggles between producing any of the cannabinoids and terpenes based
          on some regulation signal. The result of this would be a terpene and
          cannabinoid mixture. Upon speaking with Kevin Chen, the CEO of
          Hyasynth, a company that biosynthesizes cannabinoids for medicinal
          use, we received feedback that our envisioned downstream users of
          cannabinoid-based product manufacturers would prefer to have pure
          compounds shipped to them so they can generate their own custom
          mixtures. In addition, we determined that it would be too much strain
          on the yeast for a single cell to carry the genes for all the possible
          products that we may want to produce, so we decided to instead create
          one strain per product. This was after speaking with Dr. Bernard
          Duncker, a University of Waterloo professor who engineers yeast for
          their research.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Mini Jamboree
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          At the Mini Jamboree hosted by McGill and Concordia University, we
          received project feedback from judges and other teams. The main piece
          of feedback we received was that our project lacked novelty. This
          sparked a week-long pause in project work, which the team used to
          reflect on our project goals and brainstorm ways to bring novelty to
          our project. Some of our options included designing a transport system
          to extract cannabinoids more efficiently, extending our project to a
          several-year project, revisiting the idea of growing a co-culture, and
          changing our final products to uncommon terpenes and cannabinoids.
          After much discussion, we settled on adding a design for a transport
          system, the details of which are available under the Transport System
          section on the Proof of Concept page.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Modelling Goals
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Dr. Brian Ingalls, an applied math professor at the University of
          Waterloo, provided the modelling team with design, execution, and
          troubleshooting advice throughout the year. When we first designed the
          terpene pathway model, we wanted to model the conversion from terpene
          precursors to alpha pinene and beta caryophyllene. Dr. Ingalls told us
          that it would be very difficult if we couldn’t find certain kinetic
          constants, but we could still continue on by making estimations.
          Unfortunately we couldn’t find all the literature data we needed and
          after attempting to continue with estimations, we decided that the
          effort necessary to build this model component was not worth our time.
          With our limited modelling experience, we wouldn’t be able to create a
          satisfactory component with the time we had left. Instead, the members
          moved onto bioreactor modelling, a project component that we really
          wanted to do.
        </div>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          We originally had a financial analysis component planned where we were
          to compare the estimated revenues and costs associated with
          cultivation and our process, but we had to scrap it due to time
          limitations. We cut this component in particular because it depended
          on other project components like bioreactor modelling, which we did
          not start soon enough.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h3 style={{ fontSize: `${25 + fontSize}px`, color: "var(--logo)" }}>
          Tackling Medicinal vs. Recreational Use
        </h3>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Since we are tackling a controversial topic, we wanted to market our
          final products as an alternative for medicinal cannabis in an attempt
          to make the problem space more palatable to international audiences.
          Unfortunately, emailing potential end users in this aspect proved to
          be challenging. Eventually, a conversation with Professor Brad Poulos
          told us that there is no real problem space in the demand for
          medicinal cannabis. In other words, demand is only rising for
          recreational use. With this in mind, we decided to switch gears and
          face the problem of recreational cannabis head-on. This change
          resulted in the additional complexity of having the implication of
          encouraging recreational cannabis use, which we wanted to avoid, since
          we simply view our project as an alternative for cannabis cultivation,
          and nothing more. Therefore, when proposing our real-life
          implementation pipeline, we were explicit in having our final product
          being shipped to businesses rather than consumers to prevent our
          business model from being associated with encouraging a larger user
          base.
        </div>
      </ReadAloud>
      <br></br>
      <ReadAloud>
        <h2
          style={{
            fontSize: `${30 + fontSize}px`,
            color: "var(--logo)",
          }}
        >
          References
        </h2>
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Ashworth, K., & Vizuete, W. (2017). High Time to Assess the
          Environmental Impacts of Cannabis Cultivation.{" "}
          <i>Environmental Science & Technology</i>, 51(5), 2531–2533.
          <a href="https://doi.org/10.1021/acs.est.6b06343">https://doi.org/10.1021/acs.est.6b06343</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Bauer, S., Olson, J., Cockrill, A., Van Hattem, M., Miller, L.,
          Tauzer, M., & Leppig, G. (2015). Impacts of Surface Water Diversions
          for Marijuana Cultivation on Aquatic Habitat in Four Northwestern
          California Watersheds. <i>PloS ONE</i>, <i>10</i>(3), e0120016.
          <a href="https://doi.org/10.1371/journal.pone.0120016">https://doi.org/10.1371/journal.pone.0120016</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Butsic, V., Carah, J. K., Baumann, M., Stephens, C., & Brenner, J. C.
          (2018). The emergence of cannabis agriculture frontiers as
          environmental threats. <i>Environmental Research Letters</i>,{" "}
          <i>13</i>(12), 124017. <a href="https://doi.org/10.1088/1748-9326/aaeade">https://doi.org/10.1088/1748-9326/aaeade</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Carah, J. K., Howard, J. K., Thompson, S. E., Short Gianotti, A. G.,
          Bauer, S. D., Carlson, S. M., Dralle, D. N., Gabriel, M. W., Hulette,
          L. L., Johnson, B. J., Knight, C. A., Kupferberg, S. J., Martin, S. L.
          Naylor, R. L., & Power, M. E. (2015). High Time for Conservation:
          Adding the Environment to the Debate on Marijuana Liberalization.{" "}
          <i>BioScience</i>, <i>65</i>(8), 822-829.
          <a href="https://doi.org/10.1093/biosci/biv083">https://doi.org/10.1093/biosci/biv083</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Drotleff, B., Castro, C. C., Cebo, M., Dittrich, K., Fu, X., &
          Laemmerhofer, M. (2021). Untargeted LC–MS Lipidomics with Data
          Independent Acquisition using Sequential Window Acquisition of All
          Theoretical Fragment-Ion Spectra. <i>LCGC Europe</i>, <i>34</i>(1),
          7-17.
          <a href="https://www.chromatographyonline.com/view/untargeted-lc-ms-lipidomics-with-data-independent-acquisition-using-sequential-window-acquisition-of-all-theoretical-fragment-ion-spectra">https://www.chromatographyonline.com/view/untargeted-lc-ms-lipidomics-with-data-independent-acquisition-using-sequential-window-acquisition-of-all-theoretical-fragment-ion-spectra</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Fertig N., & Bade, G. (2021, August 10). An inconvenient truth (about
          weed). <i>POLITICO</i>.
          <a href="https://www.politico.com/news/2021/08/10/weed-cannabis-legalization-energy-503004">https://www.politico.com/news/2021/08/10/weed-cannabis-legalization-energy-503004</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          GAIACA. (2020, November 25).{" "}
          <i>Environmental impact of marijuana on our planet examined.</i>{" "}
          <a href="https://www.gaiaca.com/environmental-impact-of-marijuana/">https://www.gaiaca.com/environmental-impact-of-marijuana/</a>
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          Mills, E., & Zeramby, S. (2021). Energy Use by the Indoor Cannabis
          Industry: Inconvenient Truths for Producers, Consumers, and
          Policymakers. In D. Corva & J. S. Meisel (Eds.),{" "}
          <i>The Routledge Handbook of Post-Prohibition Cannabis Research</i>{" "}
          (pp. 243-265). Routledge.
        </div>
        <br />
        <div
          className="page-text"
          style={{ fontSize: `${18 + fontSize}px`, marginBottom: "2vh" }}
        >
          U.S. Energy Information Administration. (2021, October 7).{" "}
          <i>How much electricity does an American home use?</i> Retrieved
          October 9, 2022, from <a href="https://www.eia.gov/tools/faqs/faq.php?id=97&t=3">https://www.eia.gov/tools/faqs/faq.php?id=97&t=3</a>
        </div>
        <br />
      </ReadAloud>
    </div>
  );
};

export default HumanPractices;
